import Dexie from 'dexie';
import { db } from './db';
import { encrypt, decrypt } from './crypto';

// Default cache TTL: 24 hours
const DEFAULT_TTL_MS = 1000 * 60 * 60 * 24;

function serializeKey(queryKey: readonly unknown[]): string {
    return JSON.stringify(queryKey);
}

/**
 * Reads and decrypts a cached entry from IndexedDB.
 * Returns null if not found, expired, or decryption fails.
 */
export async function getCached<T>(
    queryKey: readonly unknown[],
    userId: string,
    ttlMs = DEFAULT_TTL_MS,
): Promise<T | null> {
    if (typeof window === 'undefined') return null;
    try {
        const id = serializeKey(queryKey);
        const entry = await db.cache.get(id);
        if (!entry) return null;

        const isExpired = Date.now() - entry.updatedAt > ttlMs;
        if (isExpired) {
            await db.cache.delete(id);
            return null;
        }

        return await decrypt<T>(userId, {
            data: entry.data,
            iv: entry.iv,
            salt: entry.salt,
        });
    } catch {
        return null;
    }
}

/**
 * Encrypts and stores data in IndexedDB under the given query key.
 * Reuses the existing salt when updating an entry, so the derived CryptoKey
 * (cached in memory) can be reused — avoids a fresh PBKDF2 derivation.
 */
export async function setCached<T>(
    queryKey: readonly unknown[],
    data: T,
    userId: string,
): Promise<void> {
    if (typeof window === 'undefined') return;
    try {
        const id = serializeKey(queryKey);

        // Try to reuse existing salt to hit the in-memory key cache
        const existing = await db.cache.get(id);
        const payload = existing
            ? await encrypt(userId, data, existing.salt)
            : await encrypt(userId, data);

        await db.cache.put({
            id,
            data: payload.data,
            iv: payload.iv,
            salt: payload.salt,
            updatedAt: Date.now(),
        });
    } catch (error) {
        // Best-effort: nunca lança. Mas falhar MUDO sempre é indistinguível de
        // não existir — foi assim que um RangeError no base64 escondeu por meses
        // que o cache de canal grande nunca gravava.
        console.warn('[cache] setCached falhou, entrada não gravada', error);
    }
}

/**
 * Removes all cache entries older than `ttlMs`.
 * Useful to call periodically on app startup.
 */
export async function cleanupExpiredCache(ttlMs = DEFAULT_TTL_MS): Promise<void> {
    if (typeof window === 'undefined') return;
    try {
        const cutoff = Date.now() - ttlMs;
        await db.cache.where('updatedAt').below(cutoff).delete();
    } catch {
        // Best-effort
    }
}

const LAST_USER_KEY = 'last_user_id';

export async function getLastUserId(): Promise<string | null> {
    if (typeof window === 'undefined') return null;
    try {
        const entry = await db.metadata.get(LAST_USER_KEY);
        return entry?.value ?? null;
    } catch {
        return null;
    }
}

export async function setLastUserId(userId: string): Promise<void> {
    if (typeof window === 'undefined') return;
    try {
        await db.metadata.put({ key: LAST_USER_KEY, value: userId });
    } catch {
        // best-effort
    }
}

/** Limpa o cache de queries (troca de user/logout). Preserva metadata. Apaga o DB legado se existir. */
export async function clearAllCache(): Promise<void> {
    if (typeof window === 'undefined') return;
    try {
        await db.cache.clear();
        await Dexie.delete('OmnichannelCache'); // DB do sistema antigo — apaga a cada clearAllCache; idempotente (no-op se já não existir), não é one-shot
    } catch {
        // best-effort
    }
}
